import { FormEvent, KeyboardEvent, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import PageMeta from "../components/common/PageMeta";
import PageBreadcrumb from "../components/common/PageBreadCrumb";
import RegisterLoader from "../components/common/RegisterLoader";
import { Modal } from "../components/ui/modal";
import { AppLanguage, useLanguage } from "../context/LanguageContext";
import { useAuthUser } from "../context/AuthUserContext";
import { logoutUser } from "../services/authService";
import { getLocalizedFallback } from "../utils/apiMessageI18n";
import {
  fetchUserPreferences,
  updateUserPreferences,
  UserPreferences,
} from "../services/preferencesService";
import { disableTwoFactor, generateTwoFactor, validateTwoFactor } from "../services/twoFactorService";

const DEFAULT_PREFERENCES: UserPreferences = {
  language: "pt-BR",
  timezone: "America/Sao_Paulo",
  notify_by_email: false,
  notify_by_push: false,
  notify_by_sms: false,
};

const LANGUAGE_ITEMS: { code: AppLanguage; label: string; flag: string }[] = [
  { code: "pt-BR", label: "Português (Brasil)", flag: "/images/country/flag-br.svg" },
  { code: "es", label: "Español", flag: "/images/country/flag-es.svg" },
  { code: "en", label: "English", flag: "/images/country/flag-us.svg" },
];

function isAppLanguage(value: string): value is AppLanguage {
  return value === "pt-BR" || value === "es" || value === "en";
}

function normalizeLanguageCode(value: string): AppLanguage {
  const normalized = value.trim().toLowerCase();
  if (normalized === "pt-br" || normalized === "pt_br" || normalized === "pt") {
    return "pt-BR";
  }
  if (normalized === "es" || normalized === "es-es") {
    return "es";
  }
  return "en";
}

export default function AccountSettings() {
  const navigate = useNavigate();
  const { language: currentLanguage, setLanguage } = useLanguage();
  const { user, clearUser } = useAuthUser();
  const [form, setForm] = useState<UserPreferences>(DEFAULT_PREFERENCES);
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isGeneratingTwoFactor, setIsGeneratingTwoFactor] = useState(false);
  const [isValidatingTwoFactor, setIsValidatingTwoFactor] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isTwoFactorModalOpen, setIsTwoFactorModalOpen] = useState(false);
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [twoFactorCodeDigits, setTwoFactorCodeDigits] = useState<string[]>(
    () => Array(6).fill("")
  );
  const twoFactorDigitRefs = useRef<Array<HTMLInputElement | null>>([]);
  const [twoFactorModalError, setTwoFactorModalError] = useState("");
  const [twoFactorModalSuccess, setTwoFactorModalSuccess] = useState("");
  const [twoFactorQrBase64, setTwoFactorQrBase64] = useState("");
  const [isDisableTwoFactorFlowStarted, setIsDisableTwoFactorFlowStarted] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordModalError, setPasswordModalError] = useState("");
  const [passwordModalSuccess, setPasswordModalSuccess] = useState("");
  const selectedLanguageCode = normalizeLanguageCode(form.language);
  const selectedLanguage = LANGUAGE_ITEMS.find((item) => item.code === selectedLanguageCode)!;
  const pageTitle = getLocalizedFallback(currentLanguage, {
    pt: "Configurações da conta",
    es: "Configuración de la cuenta",
    en: "Account settings",
  });
  const cardTitle = getLocalizedFallback(currentLanguage, {
    pt: "Preferências",
    es: "Preferencias",
    en: "Preferences",
  });
  const languageLabel = getLocalizedFallback(currentLanguage, {
    pt: "Idioma",
    es: "Idioma",
    en: "Language",
  });
  const notifyByEmailLabel = getLocalizedFallback(currentLanguage, {
    pt: "Notificar por e-mail",
    es: "Notificar por correo",
    en: "Notify by email",
  });
  const notifyByPushLabel = getLocalizedFallback(currentLanguage, {
    pt: "Notificar por push",
    es: "Notificar por push",
    en: "Notify by push",
  });
  const notifyBySmsLabel = getLocalizedFallback(currentLanguage, {
    pt: "Notificar por SMS",
    es: "Notificar por SMS",
    en: "Notify by SMS",
  });
  const saveButtonLabel = isSaving
    ? getLocalizedFallback(currentLanguage, {
        pt: "Salvando...",
        es: "Guardando...",
        en: "Saving...",
      })
    : getLocalizedFallback(currentLanguage, {
        pt: "Salvar configurações",
        es: "Guardar configuración",
        en: "Save settings",
      });
  const securityCardTitle = getLocalizedFallback(currentLanguage, {
    pt: "Segurança",
    es: "Seguridad",
    en: "Security",
  });
  const twoFactorTitle = getLocalizedFallback(currentLanguage, {
    pt: "Autenticação de dois fatores (2FA)",
    es: "Autenticación de dos factores (2FA)",
    en: "Two-factor authentication (2FA)",
  });
  const twoFactorDescription = getLocalizedFallback(currentLanguage, {
    pt: "Aumente a segurança da sua conta configurando 2FA.",
    es: "Aumenta la seguridad de tu cuenta configurando 2FA.",
    en: "Improve account security by setting up 2FA.",
  });
  const changePasswordTitle = getLocalizedFallback(currentLanguage, {
    pt: "Alterar Senha",
    es: "Cambiar contraseña",
    en: "Change Password",
  });
  const changePasswordDescription = getLocalizedFallback(currentLanguage, {
    pt: "Atualize a senha da sua conta.",
    es: "Actualiza la contraseña de tu cuenta.",
    en: "Update your account password.",
  });
  const activeStatusLabel = getLocalizedFallback(currentLanguage, {
    pt: "Ativo",
    es: "Activo",
    en: "Active",
  });
  const inactiveStatusLabel = getLocalizedFallback(currentLanguage, {
    pt: "Não ativo",
    es: "No activo",
    en: "Inactive",
  });
  const twoFactorModalTitle = getLocalizedFallback(currentLanguage, {
    pt: "Configurar autenticação de dois fatores",
    es: "Configurar autenticación de dos factores",
    en: "Set up two-factor authentication",
  });
  const twoFactorModalDescription = getLocalizedFallback(currentLanguage, {
    pt: "Ative o 2FA para reforçar o acesso à conta.",
    es: "Activa el 2FA para reforzar el acceso a la cuenta.",
    en: "Enable 2FA to strengthen account access.",
  });
  const startTwoFactorLabel = getLocalizedFallback(currentLanguage, {
    pt: "Iniciar 2FA",
    es: "Iniciar 2FA",
    en: "Start 2FA",
  });
  const validateTwoFactorLabel = getLocalizedFallback(currentLanguage, {
    pt: "Validar código",
    es: "Validar código",
    en: "Validate code",
  });
  const twoFactorCodeLabel = getLocalizedFallback(currentLanguage, {
    pt: "Código do autenticador",
    es: "Código del autenticador",
    en: "Authenticator code",
  });
  const disableTwoFactorLabel = getLocalizedFallback(currentLanguage, {
    pt: "Desativar 2FA",
    es: "Desactivar 2FA",
    en: "Disable 2FA",
  });
  const twoFactorHintLabel = getLocalizedFallback(currentLanguage, {
    pt: "Escaneie o QR Code no Google Authenticator ou Authy:",
    es: "Escanea el código QR en Google Authenticator o Authy:",
    en: "Scan the QR code using Google Authenticator or Authy:",
  });
  const twoFactorAlreadyEnabledLabel = getLocalizedFallback(currentLanguage, {
    pt: "O 2FA já está ativo nesta conta.",
    es: "El 2FA ya está activo en esta cuenta.",
    en: "2FA is already enabled for this account.",
  });
  const twoFactorEnabledSuccessLabel = getLocalizedFallback(currentLanguage, {
    pt: "2FA habilitado com sucesso. Você será deslogado.",
    es: "2FA habilitado con éxito. Se cerrará tu sesión.",
    en: "2FA enabled successfully. You will be signed out.",
  });
  const twoFactorDisabledSuccessLabel = getLocalizedFallback(currentLanguage, {
    pt: "2FA desativado com sucesso.",
    es: "2FA desactivado con éxito.",
    en: "2FA disabled successfully.",
  });
  const twoFactorActivateFirstLabel = getLocalizedFallback(currentLanguage, {
    pt: "Clique em iniciar para gerar o QR Code.",
    es: "Haz clic en iniciar para generar el código QR.",
    en: "Click start to generate the QR code.",
  });
  const twoFactorGeneratingLabel = getLocalizedFallback(currentLanguage, {
    pt: "Gerando QR...",
    es: "Generando QR...",
    en: "Generating QR...",
  });
  const twoFactorValidatingLabel = getLocalizedFallback(currentLanguage, {
    pt: "Validando...",
    es: "Validando...",
    en: "Validating...",
  });
  const isTwoFactorCodeComplete = twoFactorCodeDigits.every((item) => item.length === 1);
  const cancelLabel = getLocalizedFallback(currentLanguage, {
    pt: "Cancelar",
    es: "Cancelar",
    en: "Cancel",
  });
  const changePasswordModalTitle = getLocalizedFallback(currentLanguage, {
    pt: "Alterar senha",
    es: "Cambiar contraseña",
    en: "Change password",
  });
  const currentPasswordLabel = getLocalizedFallback(currentLanguage, {
    pt: "Senha atual",
    es: "Contraseña actual",
    en: "Current password",
  });
  const newPasswordLabel = getLocalizedFallback(currentLanguage, {
    pt: "Nova senha",
    es: "Nueva contraseña",
    en: "New password",
  });
  const confirmPasswordLabel = getLocalizedFallback(currentLanguage, {
    pt: "Confirmar nova senha",
    es: "Confirmar nueva contraseña",
    en: "Confirm new password",
  });
  const updatePasswordLabel = getLocalizedFallback(currentLanguage, {
    pt: "Atualizar senha",
    es: "Actualizar contraseña",
    en: "Update password",
  });

  useEffect(() => {
    const loadPreferences = async () => {
      setIsLoading(true);
      setErrorMessage("");
      try {
        const data = await fetchUserPreferences();
        if (data) {
          const normalizedLanguage = data.language
            ? normalizeLanguageCode(data.language)
            : DEFAULT_PREFERENCES.language;
          setForm((prev) => ({
            ...prev,
            ...data,
            language: normalizedLanguage,
          }));
        }
      } catch (error) {
        setErrorMessage(
          error instanceof Error
            ? error.message
            : getLocalizedFallback(currentLanguage, {
                pt: "Erro ao carregar preferências.",
                es: "Error al cargar preferencias.",
                en: "Could not load preferences.",
              })
        );
      } finally {
        setIsLoading(false);
      }
    };

    void loadPreferences();
  }, [currentLanguage]);

  useEffect(() => {
    setTwoFactorEnabled(Boolean(user?.preferences?.two_factor));
  }, [user?.preferences?.two_factor]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSaving) return;

    setIsSaving(true);
    setErrorMessage("");
    setSuccessMessage("");
    try {
      const payload: UserPreferences = {
        ...form,
        language: normalizeLanguageCode(form.language),
      };
      await updateUserPreferences(payload);
      setForm(payload);
      if (isAppLanguage(payload.language)) {
        setLanguage(payload.language);
        if (payload.language !== currentLanguage) {
          window.location.reload();
          return;
        }
      }
      setIsLanguageOpen(false);
      setSuccessMessage(
        getLocalizedFallback(currentLanguage, {
          pt: "Preferências atualizadas com sucesso.",
          es: "Preferencias actualizadas con éxito.",
          en: "Preferences updated successfully.",
        })
      );
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : getLocalizedFallback(currentLanguage, {
              pt: "Erro ao salvar preferências.",
              es: "Error al guardar preferencias.",
              en: "Could not save preferences.",
            })
      );
    } finally {
      setIsSaving(false);
    }
  };

  const openTwoFactorModal = () => {
    setTwoFactorModalError("");
    setTwoFactorModalSuccess("");
    setTwoFactorCodeDigits(Array(6).fill(""));
    setTwoFactorQrBase64("");
    setIsDisableTwoFactorFlowStarted(false);
    setIsTwoFactorModalOpen(true);
  };

  const handleAutoSignOut = async () => {
    try {
      await logoutUser();
    } finally {
      localStorage.removeItem("access_token");
      localStorage.removeItem("token_type");
      localStorage.removeItem("expires_in");
      clearUser();
      navigate("/signin");
    }
  };

  const handleGenerateTwoFactor = async () => {
    if (isGeneratingTwoFactor || isValidatingTwoFactor) return;

    setIsGeneratingTwoFactor(true);
    setTwoFactorModalError("");
    setTwoFactorModalSuccess("");
    try {
      const data = await generateTwoFactor();
      setTwoFactorQrBase64(data.qr_png_base64 || "");
      setTwoFactorCodeDigits(Array(6).fill(""));
    } catch (error) {
      setTwoFactorModalError(
        error instanceof Error
          ? error.message
          : getLocalizedFallback(currentLanguage, {
              pt: "Erro ao gerar 2FA.",
              es: "Error al generar 2FA.",
              en: "Could not generate 2FA.",
            })
      );
    } finally {
      setIsGeneratingTwoFactor(false);
    }
  };

  const handleValidateTwoFactor = async (codeOverride?: string) => {
    const twoFactorCode = (codeOverride ?? twoFactorCodeDigits.join("")).trim();
    if (isValidatingTwoFactor || !twoFactorCode || twoFactorCode.length < 6) return;
    if (!twoFactorEnabled && !twoFactorQrBase64) return;

    setIsValidatingTwoFactor(true);
    setTwoFactorModalError("");
    setTwoFactorModalSuccess("");
    try {
      if (twoFactorEnabled) {
        await disableTwoFactor(twoFactorCode.trim());
        setTwoFactorEnabled(false);
        setTwoFactorCodeDigits(Array(6).fill(""));
        setTwoFactorModalSuccess(twoFactorDisabledSuccessLabel);
        setIsTwoFactorModalOpen(false);
        return;
      }

      await validateTwoFactor(twoFactorCode.trim());
      setTwoFactorEnabled(true);
      setTwoFactorModalSuccess(twoFactorEnabledSuccessLabel);
      setTimeout(() => {
        void handleAutoSignOut();
      }, 500);
    } catch (error) {
      setTwoFactorModalError(
        error instanceof Error
          ? error.message
          : getLocalizedFallback(currentLanguage, {
              pt: "Erro ao validar 2FA.",
              es: "Error al validar 2FA.",
              en: "Could not validate 2FA.",
            })
      );
    } finally {
      setIsValidatingTwoFactor(false);
    }
  };

  const handleTwoFactorDigitChange = (index: number, value: string) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    const nextDigits = [...twoFactorCodeDigits];
    nextDigits[index] = digit;

    setTwoFactorCodeDigits((prev) => {
      const next = [...prev];
      next[index] = digit;
      return next;
    });

    if (digit && index < 5) {
      twoFactorDigitRefs.current[index + 1]?.focus();
    }

    if (nextDigits.every((item) => item.length === 1)) {
      void handleValidateTwoFactor(nextDigits.join(""));
    }
  };

  const handleTwoFactorDigitKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Backspace" && !twoFactorCodeDigits[index] && index > 0) {
      twoFactorDigitRefs.current[index - 1]?.focus();
    }
  };

  const handleTwoFactorCodePaste = (value: string) => {
    const digits = value.replace(/\D/g, "").slice(0, 6).split("");
    if (!digits.length) return;
    const filled = [...Array(6)].map((_, idx) => digits[idx] || "");
    setTwoFactorCodeDigits(filled);
    const focusIndex = Math.min(digits.length, 6) - 1;
    if (focusIndex >= 0) {
      twoFactorDigitRefs.current[focusIndex]?.focus();
    }

    if (filled.every((item) => item.length === 1)) {
      void handleValidateTwoFactor(filled.join(""));
    }
  };

  const handleOpenChangePassword = () => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setPasswordModalError("");
    setPasswordModalSuccess("");
    setIsChangePasswordModalOpen(true);
  };

  const handleSavePassword = () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordModalError(
        getLocalizedFallback(currentLanguage, {
          pt: "Preencha todos os campos.",
          es: "Completa todos los campos.",
          en: "Fill in all fields.",
        })
      );
      return;
    }
    if (newPassword.length < 8) {
      setPasswordModalError(
        getLocalizedFallback(currentLanguage, {
          pt: "A nova senha deve ter pelo menos 8 caracteres.",
          es: "La nueva contraseña debe tener al menos 8 caracteres.",
          en: "The new password must be at least 8 characters.",
        })
      );
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordModalError(
        getLocalizedFallback(currentLanguage, {
          pt: "As senhas não coincidem.",
          es: "Las contraseñas no coinciden.",
          en: "Passwords do not match.",
        })
      );
      return;
    }

    setPasswordModalError("");
    setPasswordModalSuccess(
      getLocalizedFallback(currentLanguage, {
        pt: "Senha atualizada com sucesso.",
        es: "Contraseña actualizada con éxito.",
        en: "Password updated successfully.",
      })
    );
  };

  return (
    <>
      <PageMeta
        title={`${pageTitle} | Gerify`}
        description={pageTitle}
      />
      {(isLoading || isSaving || isGeneratingTwoFactor || isValidatingTwoFactor) && <RegisterLoader />}
      <PageBreadcrumb pageTitle={pageTitle} />
      <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03] lg:p-6">
        <h3 className="mb-5 text-lg font-semibold text-gray-800 dark:text-white/90 lg:mb-7">
          {cardTitle}
        </h3>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <div>
              <p className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-400">
                {languageLabel}
              </p>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setIsLanguageOpen((prev) => !prev)}
                  className="flex h-11 w-full items-center gap-3 rounded-lg border border-gray-300 bg-transparent px-3 text-left text-sm text-gray-800 shadow-theme-xs transition hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:hover:bg-white/5"
                >
                  <img
                    src={selectedLanguage.flag}
                    alt={selectedLanguage.label}
                    className="h-5 w-5 rounded-full"
                  />
                  <span>{selectedLanguage.label}</span>
                  <svg
                    className={`ml-auto transition-transform duration-200 ${isLanguageOpen ? "rotate-180" : ""}`}
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M4 6L8 10L12 6"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                {isLanguageOpen && (
                  <ul className="absolute z-20 mt-2 w-full rounded-lg border border-gray-200 bg-white p-1 shadow-theme-lg dark:border-gray-700 dark:bg-gray-900">
                    {LANGUAGE_ITEMS.map((item) => {
                      const selected = selectedLanguageCode === item.code;
                      return (
                        <li key={item.code}>
                          <button
                            type="button"
                            onClick={() => {
                              setForm((prev) => ({ ...prev, language: item.code }));
                              setIsLanguageOpen(false);
                            }}
                            className={`flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm ${
                              selected
                                ? "bg-brand-50 text-brand-700 dark:bg-brand-500/15 dark:text-brand-300"
                                : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/5"
                            }`}
                          >
                            <img src={item.flag} alt={item.label} className="h-5 w-5 rounded-full" />
                            <span>{item.label}</span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-6">
            <div className="flex items-center gap-3">
              <input
                id="notify-by-email"
                type="checkbox"
                checked={form.notify_by_email}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, notify_by_email: e.target.checked }))
                }
                className="h-4 w-4 rounded border-gray-300 text-brand-500 focus:outline-none focus:ring-0 dark:border-gray-700 dark:bg-gray-800"
              />
              <span className="font-medium text-gray-800 text-theme-sm dark:text-white">
                {notifyByEmailLabel}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <input
                id="notify-by-push"
                type="checkbox"
                checked={form.notify_by_push}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, notify_by_push: e.target.checked }))
                }
                className="h-4 w-4 rounded border-gray-300 text-brand-500 focus:outline-none focus:ring-0 dark:border-gray-700 dark:bg-gray-800"
              />
              <span className="font-medium text-gray-800 text-theme-sm dark:text-white">
                {notifyByPushLabel}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <input
                id="notify-by-sms"
                type="checkbox"
                checked={form.notify_by_sms}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, notify_by_sms: e.target.checked }))
                }
                className="h-4 w-4 rounded border-gray-300 text-brand-500 focus:outline-none focus:ring-0 dark:border-gray-700 dark:bg-gray-800"
              />
              <span className="font-medium text-gray-800 text-theme-sm dark:text-white">
                {notifyBySmsLabel}
              </span>
            </div>
          </div>

          {errorMessage && (
            <p className="text-sm text-error-500" role="alert">
              {errorMessage}
            </p>
          )}
          {successMessage && (
            <p className="text-sm text-success-600" role="status">
              {successMessage}
            </p>
          )}

          <div>
            <button
              type="submit"
              className="inline-flex items-center justify-center rounded-lg bg-brand-500 px-4 py-3 text-sm font-medium text-white transition shadow-theme-xs hover:bg-brand-600"
            >
              {saveButtonLabel}
            </button>
          </div>
        </form>
      </div>
      <div className="mt-6 rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03] lg:p-6">
        <h3 className="mb-5 text-lg font-semibold text-gray-800 dark:text-white/90 lg:mb-7">
          {securityCardTitle}
        </h3>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <button
            type="button"
            onClick={openTwoFactorModal}
            className="relative rounded-2xl border border-gray-200 p-5 text-left transition hover:border-brand-300 dark:border-gray-800 dark:hover:border-brand-700"
          >
            <span className="absolute right-5 top-5 text-gray-500 dark:text-gray-400" aria-hidden="true">
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M9 6L15 12L9 18"
                  stroke="currentColor"
                  strokeWidth="2.75"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <p className="text-base font-semibold text-gray-800 dark:text-white/90">{twoFactorTitle}</p>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{twoFactorDescription}</p>
            <span
              className={`mt-4 inline-flex rounded-full border px-3 py-1 text-xs font-medium ${
                twoFactorEnabled
                  ? "border-success-200 text-success-600 dark:border-success-500/30 dark:text-success-400"
                  : "border-error-200 text-error-600 dark:border-error-500/30 dark:text-error-400"
              }`}
            >
              {twoFactorEnabled ? activeStatusLabel : inactiveStatusLabel}
            </span>
          </button>

          <button
            type="button"
            onClick={handleOpenChangePassword}
            className="relative rounded-2xl border border-gray-200 p-5 text-left transition hover:border-brand-300 dark:border-gray-800 dark:hover:border-brand-700"
          >
            <span className="absolute right-5 top-5 text-gray-500 dark:text-gray-400" aria-hidden="true">
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M9 6L15 12L9 18"
                  stroke="currentColor"
                  strokeWidth="2.75"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <p className="text-base font-semibold text-gray-800 dark:text-white/90">{changePasswordTitle}</p>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{changePasswordDescription}</p>
            <span className="mt-4 inline-flex rounded-full border border-gray-300 px-3 py-1 text-xs font-medium text-gray-600 dark:border-gray-700 dark:text-gray-300">
              {getLocalizedFallback(currentLanguage, {
                pt: "Configurar",
                es: "Configurar",
                en: "Configure",
              })}
            </span>
          </button>
        </div>
      </div>

      <Modal
        isOpen={isTwoFactorModalOpen}
        onClose={() => setIsTwoFactorModalOpen(false)}
        className="max-w-[520px] p-6 lg:p-8"
      >
        <h4 className="mb-2 text-xl font-semibold text-gray-800 dark:text-white/90">
          {twoFactorModalTitle}
        </h4>
        <p className="mb-5 text-sm text-gray-500 dark:text-gray-400">{twoFactorModalDescription}</p>

        {twoFactorEnabled ? (
          <div className="space-y-4">
            <p className="rounded-lg border border-success-200 bg-success-50 px-3 py-2 text-sm text-success-700 dark:border-success-500/20 dark:bg-success-500/10 dark:text-success-300">
              {twoFactorAlreadyEnabledLabel}
            </p>
            {isDisableTwoFactorFlowStarted && (
              <div className="pt-2">
                <label className="mb-1.5 block text-center text-sm font-medium text-gray-700 dark:text-gray-400">
                  {twoFactorCodeLabel}
                </label>
                <div
                  className="flex items-center justify-center gap-2"
                  onPaste={(event) => {
                    event.preventDefault();
                    handleTwoFactorCodePaste(event.clipboardData.getData("text"));
                  }}
                >
                  {[0, 1, 2, 3, 4, 5].map((index) => (
                    <div key={index} className="flex items-center">
                      {index === 3 && (
                        <span className="mx-2 text-sm font-semibold text-gray-500 dark:text-gray-400">-</span>
                      )}
                      <input
                        ref={(el) => {
                          twoFactorDigitRefs.current[index] = el;
                        }}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={twoFactorCodeDigits[index]}
                        onChange={(e) => handleTwoFactorDigitChange(index, e.target.value)}
                        onKeyDown={(e) => handleTwoFactorDigitKeyDown(index, e)}
                        className="h-11 w-11 rounded-lg border border-gray-300 bg-transparent text-center text-base font-semibold text-gray-800 shadow-theme-xs focus:border-brand-300 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <>
            {!twoFactorQrBase64 ? (
              <p className="mb-4 text-sm text-gray-600 dark:text-gray-300">
                {twoFactorActivateFirstLabel}
              </p>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-gray-700 dark:text-gray-300">{twoFactorHintLabel}</p>
                <img
                  src={`data:image/png;base64,${twoFactorQrBase64}`}
                  alt="QR Code 2FA"
                  className="mx-auto w-full max-w-[280px] rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-700"
                />
                <div className="pt-3">
                  <label className="mb-1.5 block text-center text-sm font-medium text-gray-700 dark:text-gray-400">
                    {twoFactorCodeLabel}
                  </label>
                  <div
                    className="flex items-center justify-center gap-2"
                    onPaste={(event) => {
                      event.preventDefault();
                      handleTwoFactorCodePaste(event.clipboardData.getData("text"));
                    }}
                  >
                    {[0, 1, 2, 3, 4, 5].map((index) => (
                      <div key={index} className="flex items-center">
                        {index === 3 && (
                          <span className="mx-2 text-sm font-semibold text-gray-500 dark:text-gray-400">
                            -
                          </span>
                        )}
                        <input
                          ref={(el) => {
                            twoFactorDigitRefs.current[index] = el;
                          }}
                          type="text"
                          inputMode="numeric"
                          maxLength={1}
                          value={twoFactorCodeDigits[index]}
                          onChange={(e) => handleTwoFactorDigitChange(index, e.target.value)}
                          onKeyDown={(e) => handleTwoFactorDigitKeyDown(index, e)}
                          className="h-11 w-11 rounded-lg border border-gray-300 bg-transparent text-center text-base font-semibold text-gray-800 shadow-theme-xs focus:border-brand-300 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {twoFactorModalError && (
          <p className="mt-4 text-sm text-error-500" role="alert">
            {twoFactorModalError}
          </p>
        )}
        {twoFactorModalSuccess && (
          <p className="mt-4 text-sm text-success-600" role="status">
            {twoFactorModalSuccess}
          </p>
        )}
        <div className="mt-6 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={() => setIsTwoFactorModalOpen(false)}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={() => {
              if (twoFactorEnabled) {
                if (!isDisableTwoFactorFlowStarted) {
                  setIsDisableTwoFactorFlowStarted(true);
                  setTwoFactorCodeDigits(Array(6).fill(""));
                  setTwoFactorModalError("");
                  setTwoFactorModalSuccess("");
                  setTimeout(() => {
                    twoFactorDigitRefs.current[0]?.focus();
                  }, 0);
                  return;
                }
                void handleValidateTwoFactor();
                return;
              }
              if (twoFactorQrBase64) {
                void handleValidateTwoFactor();
                return;
              }
              void handleGenerateTwoFactor();
            }}
            disabled={
              isGeneratingTwoFactor ||
              isValidatingTwoFactor ||
              (((twoFactorEnabled && isDisableTwoFactorFlowStarted) || Boolean(twoFactorQrBase64)) &&
                !isTwoFactorCodeComplete)
            }
            className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600"
          >
            {twoFactorEnabled
              ? isValidatingTwoFactor
                ? twoFactorValidatingLabel
                : disableTwoFactorLabel
              : twoFactorQrBase64
              ? isValidatingTwoFactor
                ? twoFactorValidatingLabel
                : validateTwoFactorLabel
              : isGeneratingTwoFactor
                ? twoFactorGeneratingLabel
                : startTwoFactorLabel}
          </button>
        </div>
      </Modal>

      <Modal
        isOpen={isChangePasswordModalOpen}
        onClose={() => setIsChangePasswordModalOpen(false)}
        className="max-w-[520px] p-6 lg:p-8"
      >
        <h4 className="mb-2 text-xl font-semibold text-gray-800 dark:text-white/90">
          {changePasswordModalTitle}
        </h4>
        <p className="mb-5 text-sm text-gray-500 dark:text-gray-400">{changePasswordDescription}</p>

        <div className="space-y-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-400">
              {currentPasswordLabel}
            </label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-300 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-400">
              {newPasswordLabel}
            </label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-300 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-400">
              {confirmPasswordLabel}
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-300 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
            />
          </div>
        </div>

        {passwordModalError && (
          <p className="mt-4 text-sm text-error-500" role="alert">
            {passwordModalError}
          </p>
        )}
        {passwordModalSuccess && (
          <p className="mt-4 text-sm text-success-600" role="status">
            {passwordModalSuccess}
          </p>
        )}

        <div className="mt-6 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={() => setIsChangePasswordModalOpen(false)}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={handleSavePassword}
            className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600"
          >
            {updatePasswordLabel}
          </button>
        </div>
      </Modal>
    </>
  );
}
